import { IndustryCategory } from '@/types/discovery';
import {
  Wrench,
  Home,
  Plane,
  Factory,
  Car,
  Droplets,
  Zap,
  Wind,
  Lock,
  TreePine,
  PaintBucket,
  Truck,
  HardHat,
  Waves,
  Hotel,
  MapPin,
  Cog,
  Warehouse,
  CircuitBoard,
  Utensils,
  Anchor,
  Tv,
  Refrigerator,
  WashingMachine,
  Printer,
  Hammer,
  Building2,
  Key,
  Calendar,
  Ticket,
  Music,
} from 'lucide-react';

export const industries: IndustryCategory[] = [
  {
    id: 'trades',
    slug: 'trades',
    name: 'Trades',
    description: 'Genies that book jobs and handle calls while you work',
    icon: Wrench,
    iconPath: '/icons/solutions/icon-trades.png',
    backgroundImage: '/images/discovery/bg-trades.webp',
    subcategories: [
      {
        id: 'plumbing',
        slug: 'plumbing',
        name: 'Plumbing',
        description: 'Plumbers and plumbing contractors',
        icon: Droplets,
        iconPath: '/icons/solutions/icon-plumbing.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'contact-form-2',
          'smart-escalation',
          'demo-scheduler',
        ],
        challenges: [
          'Missing after-hours emergency calls',
          'Spending too much time on phone quotes',
          'Scheduling conflicts and no-shows',
          'Difficulty capturing leads from website',
        ],
        exampleUseCases: [
          '24/7 emergency call handling',
          'Instant quote requests',
          'Service appointment booking',
          'FAQ about common plumbing issues',
        ],
      },
      {
        id: 'electrical',
        slug: 'electrical',
        name: 'Electrical',
        description: 'Electricians and electrical contractors',
        icon: Zap,
        iconPath: '/icons/solutions/icon-electrical.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'contact-form-2',
          'smart-escalation',
          'demo-scheduler',
        ],
        challenges: [
          'Emergency calls outside business hours',
          'Time spent explaining services and pricing',
          'Lead qualification before dispatch',
          'Coordinating commercial vs residential jobs',
        ],
        exampleUseCases: [
          'Emergency service dispatch',
          'Commercial vs residential routing',
          'Service quote collection',
          'Safety information and FAQs',
        ],
      },
      {
        id: 'hvac',
        slug: 'hvac',
        name: 'HVAC',
        description: 'Heating, ventilation, and air conditioning',
        icon: Wind,
        iconPath: '/icons/solutions/icon-hvac.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'contact-form-2',
          'proactive-problem-solver',
          'customer-check-in',
        ],
        challenges: [
          'Seasonal demand spikes overwhelming staff',
          'Maintenance reminder follow-ups',
          'After-hours emergency support',
          'Explaining complex system options',
        ],
        exampleUseCases: [
          'Seasonal maintenance reminders',
          'Emergency AC/heating support',
          'System troubleshooting guide',
          'Installation consultations',
        ],
      },
      {
        id: 'locksmith',
        slug: 'locksmith',
        name: 'Locksmith',
        description: 'Locksmith and security services',
        icon: Lock,
        iconPath: '/icons/solutions/icon-locksmith.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'contact-form-2',
          'smart-escalation',
        ],
        challenges: [
          '24/7 emergency lockout calls',
          'Quick lead qualification',
          'Location-based dispatch',
          'Price transparency concerns',
        ],
        exampleUseCases: [
          '24/7 emergency lockout response',
          'Quick quote for key services',
          'Commercial security consultations',
          'Service area verification',
        ],
      },
      {
        id: 'landscaping',
        slug: 'landscaping',
        name: 'Landscaping',
        description: 'Landscaping and lawn care services',
        icon: TreePine,
        iconPath: '/icons/solutions/icon-landscaping.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'contact-form-2',
          'demo-scheduler',
          'customer-check-in',
          'post-purchase-survey',
        ],
        challenges: [
          'Seasonal booking management',
          'Quote requests for custom projects',
          'Recurring service scheduling',
          'Weather-related rescheduling',
        ],
        exampleUseCases: [
          'Free estimate requests',
          'Seasonal service packages',
          'Lawn care subscriptions',
          'Project consultations',
        ],
      },
      {
        id: 'painting',
        slug: 'painting',
        name: 'Painting',
        description: 'Painters and painting contractors',
        icon: PaintBucket,
        iconPath: '/icons/solutions/icon-painting.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'contact-form-2',
          'demo-scheduler',
          'testimonial-harvester',
        ],
        challenges: [
          'Getting accurate project details for quotes',
          'Scheduling around customer availability',
          'Showcasing past work',
          'Follow-up for reviews',
        ],
        exampleUseCases: [
          'Interior vs exterior quote requests',
          'Color consultation booking',
          'Project timeline estimates',
          'Before/after showcase',
        ],
      },
    ],
  },
  {
    id: 'automotive',
    slug: 'automotive',
    name: 'Automotive',
    description: 'Genies that drive sales and service excellence',
    icon: Car,
    iconPath: '/icons/solutions/icon-automotive.png',
    backgroundImage: '/images/discovery/bg-automotive.webp',
    subcategories: [
      {
        id: 'auto-dealers',
        slug: 'auto-dealers',
        name: 'Auto Dealerships',
        description: 'New and used car dealerships',
        icon: Car,
        iconPath: '/icons/solutions/icon-auto-dealers.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'demo-scheduler',
          'personal-shopper',
          'interactive-faq',
          'exit-intent-offer',
        ],
        challenges: [
          'Test drive scheduling',
          'Inventory questions',
          'Financing inquiries',
          'Trade-in valuations',
        ],
        exampleUseCases: [
          'Test drive booking',
          'Inventory search',
          'Financing pre-qualification',
          'Trade-in estimates',
        ],
      },
      {
        id: 'auto-repair',
        slug: 'auto-repair',
        name: 'Auto Repair',
        description: 'Auto repair and maintenance shops',
        icon: Wrench,
        iconPath: '/icons/solutions/icon-auto-repair.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'demo-scheduler',
          'smart-escalation',
          'customer-check-in',
        ],
        challenges: [
          'Service appointment booking',
          'Repair estimates',
          'Status updates on vehicles',
          'Warranty questions',
        ],
        exampleUseCases: [
          'Service scheduling',
          'Repair estimates',
          'Vehicle status updates',
          'Maintenance reminders',
        ],
      },
      {
        id: 'tire-shops',
        slug: 'tire-shops',
        name: 'Tire Shops',
        description: 'Tire sales and installation',
        icon: Car,
        iconPath: '/icons/solutions/icon-tire-shops.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'personal-shopper',
          'demo-scheduler',
        ],
        challenges: [
          'Tire size questions',
          'Price comparisons',
          'Appointment scheduling',
          'Seasonal tire changeovers',
        ],
        exampleUseCases: [
          'Tire recommendations',
          'Price quotes',
          'Installation booking',
          'Seasonal reminders',
        ],
      },
    ],
  },
  {
    id: 'marine',
    slug: 'marine',
    name: 'Marine',
    description: 'Genies that navigate customer engagement',
    icon: Anchor,
    iconPath: '/icons/solutions/icon-marine.png',
    backgroundImage: '/images/discovery/bg-marine.webp',
    subcategories: [
      {
        id: 'boat-dealers',
        slug: 'boat-dealers',
        name: 'Boat Dealerships',
        description: 'New and used boat sales',
        icon: Anchor,
        iconPath: '/icons/solutions/icon-boat-dealers.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'demo-scheduler',
          'personal-shopper',
          'interactive-faq',
          'exit-intent-offer',
        ],
        challenges: [
          'Sea trial scheduling',
          'Financing inquiries',
          'Trade-in valuations',
          'Inventory questions',
        ],
        exampleUseCases: [
          'Sea trial booking',
          'Boat recommendations',
          'Financing options',
          'Trade-in estimates',
        ],
      },
      {
        id: 'marinas',
        slug: 'marinas',
        name: 'Marinas',
        description: 'Marina and dock services',
        icon: Waves,
        iconPath: '/icons/solutions/icon-marinas.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'venue-concierge',
          'proactive-welcome',
          'demo-scheduler',
        ],
        challenges: [
          'Slip availability',
          'Amenity questions',
          'Fuel dock hours',
          'Service scheduling',
        ],
        exampleUseCases: [
          'Slip reservations',
          'Amenity information',
          'Service bookings',
          'Weather updates',
        ],
      },
      {
        id: 'marine-services',
        slug: 'marine-services',
        name: 'Marine Services',
        description: 'Boat repair and maintenance',
        icon: Wrench,
        iconPath: '/icons/solutions/icon-marine-services.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'demo-scheduler',
          'smart-escalation',
          'equipment-troubleshooting',
        ],
        challenges: [
          'Service scheduling',
          'Repair estimates',
          'Parts availability',
          'Winterization timing',
        ],
        exampleUseCases: [
          'Service booking',
          'Repair quotes',
          'Parts inquiries',
          'Seasonal maintenance',
        ],
      },
    ],
  },
  {
    id: 'appliances',
    slug: 'appliances',
    name: 'Appliances',
    description: 'Genies that power up customer service',
    icon: Tv,
    iconPath: '/icons/solutions/icon-appliances.png',
    backgroundImage: '/images/discovery/bg-appliances.webp',
    subcategories: [
      {
        id: 'appliance-retail',
        slug: 'appliance-retail',
        name: 'Appliance Retail',
        description: 'Appliance sales and showrooms',
        icon: Refrigerator,
        iconPath: '/icons/solutions/icon-appliance-retail.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'personal-shopper',
          'interactive-faq',
          'demo-scheduler',
          'exit-intent-offer',
        ],
        challenges: [
          'Product comparisons',
          'Delivery scheduling',
          'Warranty questions',
          'Installation requirements',
        ],
        exampleUseCases: [
          'Product recommendations',
          'Delivery scheduling',
          'Warranty information',
          'Installation inquiries',
        ],
      },
      {
        id: 'appliance-repair',
        slug: 'appliance-repair',
        name: 'Appliance Repair',
        description: 'Appliance repair services',
        icon: WashingMachine,
        iconPath: '/icons/solutions/icon-appliance-repair.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'smart-escalation',
          'equipment-troubleshooting',
          'demo-scheduler',
        ],
        challenges: [
          'Emergency repair requests',
          'Troubleshooting guidance',
          'Parts availability',
          'Service scheduling',
        ],
        exampleUseCases: [
          'Repair scheduling',
          'Troubleshooting help',
          'Parts ordering',
          'Service estimates',
        ],
      },
      {
        id: 'electronics-retail',
        slug: 'electronics-retail',
        name: 'Electronics Retail',
        description: 'Consumer electronics sales',
        icon: Tv,
        iconPath: '/icons/solutions/icon-electronics-retail.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'personal-shopper',
          'interactive-faq',
          'exit-intent-offer',
        ],
        challenges: [
          'Product comparisons',
          'Technical specifications',
          'Availability questions',
          'Setup and support',
        ],
        exampleUseCases: [
          'Product recommendations',
          'Tech spec questions',
          'Availability checks',
          'Setup assistance',
        ],
      },
    ],
  },
  {
    id: 'office-equipment',
    slug: 'office-equipment',
    name: 'Office Equipment',
    description: 'Genies that keep your office running smoothly',
    icon: Printer,
    iconPath: '/icons/solutions/icon-office-equipment.png',
    backgroundImage: '/images/discovery/bg-office-equipment.webp',
    subcategories: [
      {
        id: 'copier-dealers',
        slug: 'copier-dealers',
        name: 'Copier & Printer Dealers',
        description: 'Office equipment sales and leasing',
        icon: Printer,
        iconPath: '/icons/solutions/icon-copier-dealers.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'demo-scheduler',
          'interactive-faq',
          'contact-form-2',
        ],
        challenges: [
          'Equipment consultations',
          'Lease vs purchase questions',
          'Service contract inquiries',
          'Upgrade recommendations',
        ],
        exampleUseCases: [
          'Equipment demos',
          'Quote requests',
          'Lease renewals',
          'Trade-in assessments',
        ],
      },
      {
        id: 'managed-print',
        slug: 'managed-print',
        name: 'Managed Print Services',
        description: 'Print fleet management and optimization',
        icon: Printer,
        iconPath: '/icons/solutions/icon-managed-print.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'equipment-troubleshooting',
          'proactive-welcome',
          'smart-escalation',
        ],
        challenges: [
          'Toner and supplies ordering',
          'Meter reading collection',
          'Service call requests',
          'Cost reporting questions',
        ],
        exampleUseCases: [
          'Supply orders',
          'Service scheduling',
          'Troubleshooting support',
          'Account inquiries',
        ],
      },
      {
        id: 'office-tech',
        slug: 'office-tech',
        name: 'Office Technology',
        description: 'Business technology solutions',
        icon: Printer,
        iconPath: '/icons/solutions/icon-office-tech.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'demo-scheduler',
          'equipment-troubleshooting',
        ],
        challenges: [
          'Technical support requests',
          'Integration questions',
          'Training inquiries',
          'System upgrades',
        ],
        exampleUseCases: [
          'Technical support',
          'Product demos',
          'Training scheduling',
          'Solution consultations',
        ],
      },
    ],
  },
  {
    id: 'home-builders',
    slug: 'home-builders',
    name: 'Home Builders',
    description: 'Genies that support homeowners from construction to living',
    icon: Home,
    iconPath: '/icons/solutions/icon-home-builders.png',
    backgroundImage: '/images/discovery/bg-home-builders.webp',
    subcategories: [
      {
        id: 'general-contractor',
        slug: 'general-contractor',
        name: 'General Contractors',
        description: 'Full-service construction and renovation',
        icon: HardHat,
        iconPath: '/icons/solutions/icon-general-contractor.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'contact-form-2',
          'demo-scheduler',
          'customer-check-in',
          'interactive-faq',
        ],
        challenges: [
          'Complex project scoping conversations',
          'Multiple stakeholder coordination',
          'Progress update requests',
          'Lead qualification for project size',
        ],
        exampleUseCases: [
          'Project inquiry qualification',
          'Consultation scheduling',
          'Progress update requests',
          'Permit and timeline FAQs',
        ],
      },
      {
        id: 'kitchen-bath',
        slug: 'kitchen-bath',
        name: 'Kitchen & Bath',
        description: 'Kitchen and bathroom remodeling specialists',
        icon: Home,
        iconPath: '/icons/solutions/icon-kitchen-bath.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'demo-scheduler',
          'personal-shopper',
          'interactive-faq',
          'testimonial-harvester',
        ],
        challenges: [
          'Showroom visit scheduling',
          'Design preference collection',
          'Budget range discussions',
          'Material and finish questions',
        ],
        exampleUseCases: [
          'Showroom appointment booking',
          'Design consultation requests',
          'Material selection guidance',
          'Project inspiration gallery',
        ],
      },
      {
        id: 'roofing',
        slug: 'roofing',
        name: 'Roofing',
        description: 'Roofing installation and repair',
        icon: Home,
        iconPath: '/icons/solutions/icon-roofing.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'contact-form-2',
          'smart-escalation',
        ],
        challenges: [
          'Storm damage emergency calls',
          'Insurance claim assistance',
          'Free inspection scheduling',
          'Material and warranty questions',
        ],
        exampleUseCases: [
          'Emergency roof repair dispatch',
          'Free inspection booking',
          'Insurance claim guidance',
          'Material warranty FAQs',
        ],
      },
      {
        id: 'flooring',
        slug: 'flooring',
        name: 'Flooring',
        description: 'Flooring installation and refinishing',
        icon: Hammer,
        iconPath: '/icons/solutions/icon-flooring.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'demo-scheduler',
          'personal-shopper',
          'contact-form-2',
        ],
        challenges: [
          'Material selection guidance',
          'Square footage estimates',
          'Showroom visit scheduling',
          'Installation timeline questions',
        ],
        exampleUseCases: [
          'Material recommendations',
          'Free estimate requests',
          'Showroom appointments',
          'Care and maintenance tips',
        ],
      },
    ],
  },
  {
    id: 'travel-hospitality',
    slug: 'travel-hospitality',
    name: 'Travel & Hospitality',
    description: 'Genies that elevate every guest experience',
    icon: Plane,
    iconPath: '/icons/solutions/icon-travel-hospitality.png',
    backgroundImage: '/images/discovery/bg-travel-hospitality.webp',
    subcategories: [
      {
        id: 'hotels',
        slug: 'hotels',
        name: 'Hotels & Resorts',
        description: 'Hotels, resorts, and accommodations',
        icon: Hotel,
        iconPath: '/icons/solutions/icon-hotels.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'venue-concierge',
          'proactive-welcome',
          'customer-check-in',
          'post-purchase-survey',
        ],
        challenges: [
          'High volume of reservation inquiries',
          'Concierge service requests',
          'Local recommendations',
          'Guest satisfaction follow-up',
        ],
        exampleUseCases: [
          'Reservation assistance',
          'Virtual concierge service',
          'Local area recommendations',
          'Amenity information',
        ],
      },
      {
        id: 'travel-agency',
        slug: 'travel-agency',
        name: 'Travel Agencies',
        description: 'Travel planning and booking services',
        icon: Plane,
        iconPath: '/icons/solutions/icon-travel-agency.png',
        recommendedPlaybookIds: [
          'proactive-welcome',
          'demo-scheduler',
          'personal-shopper',
          'interactive-faq',
          'lead-magnet-delivery',
        ],
        challenges: [
          'Trip preference collection',
          'Complex itinerary questions',
          'Budget qualification',
          'Destination recommendations',
        ],
        exampleUseCases: [
          'Trip planning consultations',
          'Destination recommendations',
          'Package comparisons',
          'Travel requirement checklists',
        ],
      },
      {
        id: 'tours-activities',
        slug: 'tours-activities',
        name: 'Tours & Activities',
        description: 'Tour operators and activity providers',
        icon: MapPin,
        iconPath: '/icons/solutions/icon-tours-activities.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'demo-scheduler',
          'venue-concierge',
        ],
        challenges: [
          'Tour availability questions',
          'Group booking coordination',
          'Weather-related changes',
          'Accessibility accommodations',
        ],
        exampleUseCases: [
          'Tour booking assistance',
          'Group reservation handling',
          'Itinerary information',
          'Accessibility questions',
        ],
      },
    ],
  },
  {
    id: 'manufacturing',
    slug: 'manufacturing',
    name: 'Manufacturing',
    description: 'Genies that streamline operations and boost productivity',
    icon: Factory,
    iconPath: '/icons/solutions/icon-manufacturing.png',
    backgroundImage: '/images/discovery/bg-manufacturing.webp',
    subcategories: [
      {
        id: 'general-manufacturing',
        slug: 'general-manufacturing',
        name: 'General Manufacturing',
        description: 'Manufacturing and production facilities',
        icon: Factory,
        iconPath: '/icons/solutions/icon-general-manufacturing.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'contact-form-2',
          'demo-scheduler',
          'internal-learning-assistant',
        ],
        challenges: [
          'Custom quote requests',
          'Lead time inquiries',
          'Quality certifications',
          'Capability questions',
        ],
        exampleUseCases: [
          'RFQ submissions',
          'Lead time estimates',
          'Capability inquiries',
          'Quality certifications',
        ],
      },
      {
        id: 'electronics',
        slug: 'electronics',
        name: 'Electronics Manufacturing',
        description: 'Electronic components and assembly',
        icon: CircuitBoard,
        iconPath: '/icons/solutions/icon-electronics-mfg.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'contact-form-2',
          'equipment-troubleshooting',
        ],
        challenges: [
          'Technical specifications',
          'Custom design inquiries',
          'MOQ and pricing',
          'Compliance certifications',
        ],
        exampleUseCases: [
          'Component specifications',
          'Custom design requests',
          'Pricing inquiries',
          'Compliance questions',
        ],
      },
      {
        id: 'food-beverage',
        slug: 'food-beverage',
        name: 'Food & Beverage',
        description: 'Food and beverage production',
        icon: Utensils,
        iconPath: '/icons/solutions/icon-food-beverage.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'contact-form-2',
          'customer-check-in',
        ],
        challenges: [
          'Distribution inquiries',
          'Product availability',
          'Private label requests',
          'Allergen and ingredient info',
        ],
        exampleUseCases: [
          'Distribution partnerships',
          'Product inquiries',
          'Private label requests',
          'Ingredient information',
        ],
      },
    ],
  },
  {
    id: 'industrial',
    slug: 'industrial',
    name: 'Industrial',
    description: 'Genies that keep operations running 24/7',
    icon: Factory,
    iconPath: '/icons/solutions/icon-industrial.png',
    backgroundImage: '/images/discovery/bg-industrial.webp',
    subcategories: [
      {
        id: 'logistics',
        slug: 'logistics',
        name: 'Logistics & Freight',
        description: 'Shipping, freight, and logistics companies',
        icon: Truck,
        iconPath: '/icons/solutions/icon-logistics.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'status-page-communicator',
          'proactive-welcome',
          'contact-form-2',
        ],
        challenges: [
          'Shipment tracking inquiries',
          'Quote requests for freight',
          'Delivery scheduling',
          'Claims and issue resolution',
        ],
        exampleUseCases: [
          'Shipment status tracking',
          'Freight quote requests',
          'Delivery scheduling',
          'Claims submission',
        ],
      },
      {
        id: 'warehousing',
        slug: 'warehousing',
        name: 'Warehousing',
        description: 'Warehouse and storage services',
        icon: Warehouse,
        iconPath: '/icons/solutions/icon-warehousing.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'contact-form-2',
          'demo-scheduler',
        ],
        challenges: [
          'Space availability inquiries',
          'Inventory questions',
          'Access scheduling',
          'Service pricing',
        ],
        exampleUseCases: [
          'Storage space inquiries',
          'Inventory management questions',
          'Facility tour scheduling',
          'Service capabilities',
        ],
      },
      {
        id: 'industrial-equipment',
        slug: 'industrial-equipment',
        name: 'Industrial Equipment',
        description: 'Industrial equipment sales and service',
        icon: Cog,
        iconPath: '/icons/solutions/icon-industrial-equipment.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'equipment-troubleshooting',
          'proactive-welcome',
          'demo-scheduler',
          'smart-escalation',
        ],
        challenges: [
          'Technical support requests',
          'Parts ordering',
          'Service scheduling',
          'Equipment specifications',
        ],
        exampleUseCases: [
          'Equipment troubleshooting',
          'Parts inquiries',
          'Service scheduling',
          'Technical specifications',
        ],
      },
    ],
  },
  {
    id: 'mining',
    slug: 'mining',
    name: 'Mining',
    description: 'Genies that keep mines running 24/7',
    icon: HardHat,
    iconPath: '/icons/solutions/icon-mining.png',
    backgroundImage: '/images/discovery/bg-mining.webp',
    subcategories: [
      {
        id: 'mining-operations',
        slug: 'mining-operations',
        name: 'Mining Operations',
        description: 'Mine operators, equipment, contractors and site services',
        icon: Cog,
        iconPath: '/icons/solutions/icon-mining-operations.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'equipment-troubleshooting',
          'proactive-welcome',
          'smart-escalation',
          'contact-form-2',
        ],
        challenges: [
          'Unplanned equipment downtime at remote sites',
          'Parts and consumables sourcing without part numbers',
          'Safety and near-miss reporting friction',
          'Contractor callouts and shift coordination',
        ],
        exampleUseCases: [
          'Breakdown triage and dispatch',
          'Parts and consumables ordering',
          'Safety incident intake',
          'Contractor and shift coordination',
        ],
      },
    ],
  },
  {
    id: 'real-estate',
    slug: 'real-estate',
    name: 'Real Estate',
    description: 'Genies that never miss a listing inquiry',
    icon: Building2,
    iconPath: '/icons/solutions/icon-real-estate.png',
    backgroundImage: '/images/discovery/bg-real-estate.webp',
    subcategories: [
      {
        id: 'residential-agents',
        slug: 'residential-agents',
        name: 'Residential Agents',
        description: 'Home sales and buyer representation',
        icon: Home,
        iconPath: '/icons/solutions/icon-residential-agents.png',
        recommendedPlaybookIds: [
          'lead-capture',
          'appointment-scheduler',
          'interactive-faq',
          'proactive-welcome',
        ],
        challenges: [
          'Missing listing inquiries during showings and after hours',
          'Time-consuming showing coordination and scheduling',
          'Wasting time on unqualified or uncommitted buyers',
          'Losing speed-to-lead race to competing agents',
        ],
        exampleUseCases: [
          'Listing inquiry capture 24/7',
          'Automated showing scheduling',
          'Buyer pre-qualification',
          'Open house follow-up',
        ],
      },
      {
        id: 'commercial-real-estate',
        slug: 'commercial-real-estate',
        name: 'Commercial Real Estate',
        description: 'Office, retail, and industrial property',
        icon: Building2,
        iconPath: '/icons/solutions/icon-commercial-real-estate.png',
        recommendedPlaybookIds: [
          'lead-capture',
          'interactive-faq',
          'demo-scheduler',
          'contact-form-2',
        ],
        challenges: [
          'Complex multi-stakeholder inquiries',
          'Long sales cycles requiring persistent follow-up',
          'Detailed property specification questions',
          'Tenant and lease management inquiries',
        ],
        exampleUseCases: [
          'Commercial listing inquiries',
          'Property tour scheduling',
          'Lease term and pricing questions',
          'Tenant support and maintenance requests',
        ],
      },
      {
        id: 'property-management',
        slug: 'property-management',
        name: 'Property Management',
        description: 'Rental and property management services',
        icon: Key,
        iconPath: '/icons/solutions/icon-property-management.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'smart-escalation',
          'proactive-welcome',
          'customer-check-in',
        ],
        challenges: [
          'After-hours tenant emergency calls',
          'High volume of maintenance requests',
          'Vacancy listing and showing coordination',
          'Rent payment and lease renewal questions',
        ],
        exampleUseCases: [
          'Maintenance request intake',
          'Emergency dispatch and triage',
          'Vacancy showing scheduling',
          'Lease and payment FAQs',
        ],
      },
    ],
  },
  {
    id: 'events',
    slug: 'events',
    name: 'Events',
    description: 'Genies that make every event unforgettable',
    icon: Ticket,
    iconPath: '/icons/solutions/icon-events.png',
    backgroundImage: '/images/discovery/bg-events.webp',
    subcategories: [
      {
        id: 'venues',
        slug: 'venues',
        name: 'Venues & Spaces',
        description: 'Event venues, halls, and conference centers',
        icon: Hotel,
        iconPath: '/icons/solutions/icon-venues.png',
        recommendedPlaybookIds: [
          'lead-capture',
          'appointment-scheduler',
          'interactive-faq',
          'proactive-welcome',
        ],
        challenges: [
          'High volume of availability and pricing inquiries',
          'Coordinating multiple events and vendor schedules',
          'After-hours booking requests from corporate clients',
          'Managing event-day logistics and last-minute changes',
        ],
        exampleUseCases: [
          'Venue availability and pricing',
          'Event booking and scheduling',
          'Vendor load-in coordination',
          'Day-of attendee support',
        ],
      },
      {
        id: 'event-planners',
        slug: 'event-planners',
        name: 'Event Planners',
        description: 'Wedding, corporate, and social event planning',
        icon: Calendar,
        iconPath: '/icons/solutions/icon-event-planners.png',
        recommendedPlaybookIds: [
          'lead-capture',
          'interactive-faq',
          'demo-scheduler',
          'contact-form-2',
        ],
        challenges: [
          'Juggling inquiries for multiple simultaneous events',
          'Client expectation management and scope questions',
          'Vendor coordination during event crunch time',
          'Post-event feedback collection and follow-up',
        ],
        exampleUseCases: [
          'New client inquiry capture',
          'Package and pricing information',
          'Vendor scheduling coordination',
          'Post-event feedback collection',
        ],
      },
      {
        id: 'concerts-live-shows',
        slug: 'concerts-live-shows',
        name: 'Concerts & Live Shows',
        description: 'Music venues, festivals, and live entertainment',
        icon: Music,
        iconPath: '/icons/solutions/icon-concerts-live-shows.png',
        recommendedPlaybookIds: [
          'interactive-faq',
          'proactive-welcome',
          'smart-escalation',
          'venue-concierge',
        ],
        challenges: [
          'Massive ticket inquiry volume before and during shows',
          'Real-time schedule and lineup questions on event day',
          'Parking, directions, and accessibility questions',
          'VIP and backstage access coordination',
        ],
        exampleUseCases: [
          'Ticket availability and pricing',
          'Event schedule and lineup info',
          'Venue directions and parking',
          'VIP and group booking',
        ],
      },
    ],
  },
];

// Helper functions
export const getIndustryBySlug = (slug: string): IndustryCategory | undefined => {
  return industries.find(i => i.slug === slug);
};

export const getSubcategoryBySlug = (
  industrySlug: string,
  subcategorySlug: string
): { industry: IndustryCategory; subcategory: import('@/types/discovery').BusinessSubcategory } | undefined => {
  const industry = getIndustryBySlug(industrySlug);
  if (!industry) return undefined;

  const subcategory = industry.subcategories.find(s => s.slug === subcategorySlug);
  if (!subcategory) return undefined;

  return { industry, subcategory };
};

export const getAllSubcategories = () => {
  return industries.flatMap(industry =>
    industry.subcategories.map(sub => ({
      ...sub,
      industryId: industry.id,
      industrySlug: industry.slug,
      industryName: industry.name,
    }))
  );
};
